import { useState, useCallback, useEffect, useRef } from 'react';

export function useAutoRefresh(fetchFn, intervalMs, options = {}) {
  const { enabled = true, key = '' } = options;
  const defaultInterval = intervalMs || 30000;
  const [lastUpdated, setLastUpdated] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [dataChanged, setDataChanged] = useState(false);
  const intervalRef = useRef(null);
  const currentIntervalMs = useRef(defaultInterval);
  const lastHiddenAt = useRef(null);
  const lastActivityAt = useRef(Date.now());
  const consecutiveErrors = useRef(0);
  const lastDataHash = useRef(null);
  const mountedRef = useRef(true);
  const fetchFnRef = useRef(fetchFn);
  fetchFnRef.current = fetchFn;

  const hashData = useCallback((data) => {
    try { return JSON.stringify(data); } catch { return String(Date.now()); }
  }, []);

  const doFetch = useCallback(async (isManual) => {
    if (!mountedRef.current) return;
    setIsRefreshing(true);
    try {
      const result = await fetchFnRef.current();
      if (!mountedRef.current) return;
      const newHash = hashData(result);
      if (lastDataHash.current !== null && lastDataHash.current !== newHash) {
        setDataChanged(true);
        setTimeout(() => { if (mountedRef.current) setDataChanged(false); }, 1500);
      }
      lastDataHash.current = newHash;
      consecutiveErrors.current = 0;
      currentIntervalMs.current = defaultInterval;
      setLastUpdated(Date.now());
    } catch (err) {
      if (!mountedRef.current) return;
      consecutiveErrors.current++;
      currentIntervalMs.current = Math.min(currentIntervalMs.current * 2, 120000);
      console.warn('[AutoRefresh] Error (' + consecutiveErrors.current + '):', err.message);
    }
    if (mountedRef.current) setIsRefreshing(false);
  }, [defaultInterval, hashData]);

  useEffect(() => {
    const onActivity = () => { lastActivityAt.current = Date.now(); };
    const events = ['mousedown', 'keydown', 'touchstart', 'scroll'];
    events.forEach(e => document.addEventListener(e, onActivity, { passive: true }));
    return () => events.forEach(e => document.removeEventListener(e, onActivity));
  }, []);

  useEffect(() => {
    if (!enabled) return;
    const startPolling = () => {
      clearInterval(intervalRef.current);
      intervalRef.current = setInterval(() => {
        if (document.hidden) return;
        if (Date.now() - lastActivityAt.current > 300000) return;
        doFetch(false);
      }, currentIntervalMs.current);
    };
    const onVisibilityChange = () => {
      if (document.hidden) {
        lastHiddenAt.current = Date.now();
        clearInterval(intervalRef.current);
      } else {
        const hiddenDuration = lastHiddenAt.current ? Date.now() - lastHiddenAt.current : 0;
        if (hiddenDuration > 30000) doFetch(false);
        startPolling();
      }
    };
    doFetch(false);
    startPolling();
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      clearInterval(intervalRef.current);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [enabled, doFetch]);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const refresh = useCallback(() => doFetch(true), [doFetch]);

  return { lastUpdated, isRefreshing, refresh, dataChanged };
}

export function useIsMobile(breakpoint = 768) {
  const [mobile, setMobile] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth < breakpoint : false
  );
  useEffect(() => {
    const handler = () => setMobile(window.innerWidth < breakpoint);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [breakpoint]);
  return mobile;
}

export function useConfirm() {
  const [state, setState] = useState(null);

  const confirm = useCallback((opts) => {
    return new Promise((resolve) => {
      setState({ ...opts, resolve });
    });
  }, []);

  const handleConfirm = useCallback(() => {
    state?.resolve(true);
    setState(null);
  }, [state]);

  const handleCancel = useCallback(() => {
    state?.resolve(false);
    setState(null);
  }, [state]);

  return { confirm, confirmState: state, handleConfirm, handleCancel };
}

export function useDeferredLoading(loading, delay = 200) {
  const [show, setShow] = useState(false);
  useEffect(() => {
    if (loading) {
      const timer = setTimeout(() => setShow(true), delay);
      return () => clearTimeout(timer);
    }
    setShow(false);
  }, [loading, delay]);
  return show;
}
